import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  getConsent,
  setConsent,
  COOKIE_PREFS_EVENT,
  type CookieConsent,
} from '../lib/cookieConsent';
import { grantAnalytics, denyAnalytics } from '../lib/analytics';

// GDPR / Garante-compliant consent banner: equal-prominence Accetta/Rifiuta,
// nothing pre-ticked, closing (X) = refuse, granular "Personalizza" panel, and a
// "Preferenze cookie" footer entry point (via the COOKIE_PREFS_EVENT) to reopen it.
const CookieBanner: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  // Re-apply the saved decision on load (re-grant analytics if previously accepted).
  useEffect(() => {
    const consent = getConsent();
    if (consent === null) {
      setOpen(true);
    } else if (consent === 'accepted') {
      grantAnalytics();
    }
  }, []);

  // Footer "Preferenze cookie" reopens the banner on the details view.
  useEffect(() => {
    const reopen = () => {
      setAnalytics(getConsent() === 'accepted');
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(COOKIE_PREFS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, reopen);
  }, []);

  const apply = useCallback((value: CookieConsent) => {
    setConsent(value);
    if (value === 'accepted') grantAnalytics();
    else denyAnalytics();
    setOpen(false);
    setDetails(false);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-label="Preferenze sui cookie"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6"
        >
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card text-card-foreground shadow-2xl">
            <div className="flex items-start gap-4 p-5 sm:p-6">
              <span className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 sm:flex">
                <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
              </span>

              <div className="flex-1">
                <h2 className="font-heading text-lg font-bold text-text-primary">
                  Rispettiamo la tua privacy
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">
                  Usiamo cookie tecnici, necessari al funzionamento del sito, e — solo con il tuo
                  consenso — cookie analitici (Google Analytics) per capire come viene utilizzato il
                  sito. Non usiamo cookie di profilazione o marketing. Maggiori dettagli nella{' '}
                  <Link to="/cookie-policy" className="font-medium text-primary hover:underline">
                    Cookie Policy
                  </Link>{' '}
                  e nella{' '}
                  <Link to="/privacy" className="font-medium text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>

                {details && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-muted/40 px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Cookie tecnici</p>
                        <p className="text-xs text-text-secondary">
                          Necessari al funzionamento del sito.
                        </p>
                      </div>
                      <span className="text-xs font-medium text-text-secondary">Sempre attivi</span>
                    </div>

                    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-border bg-muted/40 px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Cookie analitici</p>
                        <p className="text-xs text-text-secondary">
                          Google Analytics 4, dati in forma aggregata.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={analytics}
                        onChange={(e) => setAnalytics(e.target.checked)}
                        className="h-5 w-5 accent-primary"
                        aria-label="Attiva i cookie analitici"
                      />
                    </label>
                  </div>
                )}

                <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    onClick={() => apply('accepted')}
                    variant="gradient"
                    className="rounded-full font-medium sm:flex-1"
                  >
                    Accetta tutti
                  </Button>
                  <Button
                    onClick={() => apply('rejected')}
                    variant="outline"
                    className="rounded-full font-medium sm:flex-1"
                  >
                    Rifiuta
                  </Button>
                  {details ? (
                    <Button
                      onClick={() => apply(analytics ? 'accepted' : 'rejected')}
                      variant="secondary"
                      className="rounded-full font-medium sm:flex-1"
                    >
                      Salva preferenze
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setDetails(true)}
                      variant="ghost"
                      className="rounded-full font-medium text-text-secondary"
                    >
                      Personalizza
                    </Button>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => apply('rejected')}
                aria-label="Chiudi e rifiuta i cookie non necessari"
                className="flex-shrink-0 rounded-full p-1.5 text-text-secondary transition-colors hover:bg-muted hover:text-text-primary"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
